import React, { useState, useEffect, useMemo, useRef } from "react";
import AsyncSelect from "react-select/async";
import Select from "react-select";
import axios from "axios";

let selectBorderColor = "#CED4DA";
const selectStyle = {
  control: (base, state) => ({
    ...base,
    border: `1px solid ${selectBorderColor}`,
    boxShadow: "none",
    "&:hover": {
      border: `1px solid ${selectBorderColor}`,
    },
    color: "#1f0e3c",
    fontFamily: "Montserrat",
    fontWeight: 400,
    fontSize: "12px",
    height: "38px",
    borderRadius: 0,
  }),
  singleValue: (provided) => ({
    ...provided,
    color: "#1f0e3c",
  }),
  input: (base) => ({
    ...base,
    color: "#1f0e3c",
  }),
  option: (provided, state) => ({
    ...provided,
    fontSize: "12px",
    backgroundColor: state.isSelected ? "#7D0052" : "inherit",
    "&:hover": { backgroundColor: state.isSelected ? "#7D0052" : "#ECECEC" },
  }),
  menu: (base) => ({
    ...base,
    fontSize: "12px",
    borderRadius: 0,
  }),
};

export function CustomSelect({ question, t }) {
  if (question.name === "job_title") {
    return <JobTitleSelect question={question} t={t} />;
  }

  return <InstitutionSelect question={question} t={t} />;
}

function JobTitleSelect({ question, t }) {
  const jobTitleOptions = useMemo(
    () => [
      { value: t("job-title.options.other"), label: t("job-title.options.other") },
      { value: t("job-title.options.academicStaff"), label: t("job-title.options.academicStaff") },
      { value: t("job-title.options.administrativeStaff"), label: t("job-title.options.administrativeStaff") },
      { value: t("job-title.options.regulator"), label: t("job-title.options.regulator") },
      { value: t("job-title.options.researcher"), label: t("job-title.options.researcher") },
    ],
    [t]
  );

  const questionValue = question?.value ?? null;
  const [jobTitleValue, setJobTitleValue] = useState(questionValue);
  const isUserUpdate = useRef(false);

  useEffect(() => {
    if (isUserUpdate.current) {
      isUserUpdate.current = false;
      return;
    }

    if (questionValue !== jobTitleValue) {
      setJobTitleValue(questionValue);
    }
  }, [questionValue, jobTitleValue]);

  useEffect(() => {
    question.value = jobTitleValue;
  }, [question, jobTitleValue]);

  const selectedJobTitle = useMemo(() => {
    if (!jobTitleValue) return null;

    return jobTitleOptions.find((option) => option.value === jobTitleValue) || null;
  }, [jobTitleOptions, jobTitleValue]);

  return (
    <Select
      name={question.name}
      value={selectedJobTitle}
      onChange={(option) => {
        isUserUpdate.current = true;
        setJobTitleValue(option ? option.value : null);
      }}
      options={jobTitleOptions}
      styles={{
        ...selectStyle,
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
      }}
      placeholder={t("job-title.placeholder")}
      menuPortalTarget={document.body}
    />
  );
}

function InstitutionSelect({ question, t }) {
  const [selectedInstitutionValue, setSelectedInstitutionValue] =
    useState(null);
  const [selectedCountryValue, setSelectedCountryValue] = useState(null);
  const [institutionValue, setInstitutionValue] = useState(null);
  const [doInstitutionCreation, setDoInstitutionCreation] = useState(false);

  const loadInstitutionOptions = (inputValue) => {
    return axios
      .get("/institutions/for_select?institution=" + inputValue)
      .then((res) => {
        const institutions = res.data.data;

        return institutions !== null &&
          institutions !== undefined &&
          institutions.length > 0
          ? [
              ...institutions,
              {
                value: "create_new_institution",
                label: t("Other"),
              },
            ]
          : [
              {
                value: "create_new_institution",
                label: t("Other"),
              },
            ];
      });
  };

  const handleInstitutionSelectChange = (value) => {
    setSelectedInstitutionValue(value);

    if (value.value === "create_new_institution") {
      setDoInstitutionCreation(true);
    } else {
      setDoInstitutionCreation(false);
    }
  };

  const handleCountrySelectChange = (value) => {
    setSelectedCountryValue(value);
  };

  const loadCountryOptions = (inputValue) => {
    return axios
      .get("/collections/countries?input_value=" + inputValue)
      .then((res) => {
        return res.data.data.map((country) => {
          return { label: country.name, value: country.id };
        });
      });
  };

  const handleCustomInstitutionNameChange = (e) => {
    const value = e.target.value;

    setInstitutionValue(value);
  };

  useEffect(() => {
    if (selectedInstitutionValue) {
      let answer = { institution: selectedInstitutionValue };

      if (doInstitutionCreation) {
        if (!!institutionValue && !!selectedCountryValue) {
          answer = {
            ...answer,
            custom_institution_name: institutionValue,
            custom_institution_country_id: selectedCountryValue,
          };
        } else {
          answer = {}
        }
      }

      question.value = answer;
    }
  }, [selectedInstitutionValue, question, institutionValue, selectedCountryValue]);

  return (
    <div className="custom-select-container">
      <AsyncSelect
        name={question.name}
        value={selectedInstitutionValue}
        cacheOptions
        loadOptions={loadInstitutionOptions}
        onChange={handleInstitutionSelectChange}
        styles={{
          ...selectStyle,
          menuPortal: base => ({ ...base, zIndex: 9999 })
        }}
        placeholder={t("institution-placeholder")}
        noOptionsMessage={({ inputValue }) =>
          inputValue.length === 0 ? t("start-typing-message") : t("no-options-message")
        }
        menuPortalTarget={document.body}
      />
      <div
        className="custom-institution-container"
        style={{ display: doInstitutionCreation ? "flex" : "none" }}
      >
        <div className="field-container">
          <label>{t("institution-label")}</label>
          <div id="lop">
            <input
              name="custom_institution_name"
              id="custom_institution_name"
              onChange={handleCustomInstitutionNameChange}
              placeholder={t("institution-placeholder")}
              className="form-control"
            />
          </div>
        </div>

        <div className="field-container">
          <label>{t("country-label")}</label>
          <div id="lop">
            <AsyncSelect
              name="custom_institution_country_id"
              value={selectedCountryValue}
              loadOptions={loadCountryOptions}
              onChange={handleCountrySelectChange}
              styles={selectStyle}
              placeholder={t("country-placeholder")}
              noOptionsMessage={({ inputValue }) =>
                inputValue.length === 0 ? t("start-typing-message") : t("no-options-message")
              }
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
